import { useState } from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import type { ISignalProviderListCard } from '@brushfire/types'
import {
  sensemakerTableBodyCellSx,
  sensemakerTableBodyCellInteractiveSx,
  sensemakerTableCardSx,
  sensemakerTableHeaderCellSx,
  sensemakerTableSx,
  SortableTableHeaderLabel,
  SENSEMAKER_TABLE,
} from '@/shared/table'
import { formatUsPhoneCell } from '@/shared/lib/formatUsNanpDisplay'
import {
  SP_LIST_DEFAULT_ROWS_PER_PAGE,
  SP_LIST_ROWS_PER_PAGE_OPTIONS,
  SP_LIST_TABLE_PAGINATION_TOP_GAP_PX,
} from '../lib/signal-provider-list-layout'
import type { SignalProviderSortState, SignalProviderUserSortColumn } from '../lib/signal-provider-list-sort'
import { sortDirectionForColumn, toggleSortColumn } from '../lib/signal-provider-list-sort'
import {
  signalProviderDeactivateMenuItemSx,
  signalProviderReactivateMenuItemSx,
  signalProviderRowActionsMenuPaperSx,
} from '../lib/signal-provider-row-actions-menu'
import { SignalProviderVerifiedPill } from './SignalProviderVerifiedPill'

/** Fixed column widths — sort / data changes must not reflow headers. */
const SP_TABLE_COL_WIDTHS = [
  '15%', // SP ID — full SP-U-#####-MMDD
  '10%',
  '10%',
  '9%', // State — header + sort icon
  '18%',
  '12%',
  '14%', // Verified Status — full header; pill stays compact
  '12%',
] as const

const spIdCellSx = {
  ...sensemakerTableBodyCellSx,
  fontWeight: 500,
  color: SENSEMAKER_TABLE.primaryTextColor,
  overflow: 'visible',
  textOverflow: 'clip',
  whiteSpace: 'nowrap',
  maxWidth: 'none',
} as const

const noEllipsisHeaderCellSx = {
  ...sensemakerTableHeaderCellSx,
  overflow: 'visible',
  maxWidth: 'none',
  whiteSpace: 'nowrap',
} as const

const verifiedStatusCellSx = {
  ...sensemakerTableBodyCellInteractiveSx,
  whiteSpace: 'nowrap',
} as const

function SelectDropdownIcon(props: object) {
  return <KeyboardArrowDownIcon {...props} sx={{ fontSize: 20, color: '#4a5565', mr: '12px' }} />
}

function EyeActionIcon() {
  return (
    <svg width="22" height="18" viewBox="0 0 21.6373 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10.8187 0C16.2108 0 20.6968 3.87976 21.6373 9C20.6968 14.1202 16.2108 18 10.8187 18C5.42648 18 0.94051 14.1202 0 9C0.94051 3.87976 5.42648 0 10.8187 0ZM10.8187 16C15.0543 16 18.6787 13.052 19.5961 9C18.6787 4.94803 15.0543 2 10.8187 2C6.58296 2 2.95858 4.94803 2.04114 9C2.95858 13.052 6.58296 16 10.8187 16ZM10.8187 13.5C8.33334 13.5 6.31862 11.4853 6.31862 9C6.31862 6.51472 8.33334 4.5 10.8187 4.5C13.3039 4.5 15.3187 6.51472 15.3187 9C15.3187 11.4853 13.3039 13.5 10.8187 13.5ZM10.8187 11.5C12.1994 11.5 13.3187 10.3807 13.3187 9C13.3187 7.6193 12.1994 6.5 10.8187 6.5C9.43796 6.5 8.31862 7.6193 8.31862 9C8.31862 10.3807 9.43796 11.5 10.8187 11.5Z"
        fill="#1976D2"
      />
    </svg>
  )
}

interface Props {
  rows: ISignalProviderListCard[]
  totalFiltered: number
  page: number
  rowsPerPage: number
  sort: SignalProviderSortState
  onSortChange: (next: SignalProviderSortState) => void
  onPageChange: (page: number) => void
  onRowsPerPageChange: (rowsPerPage: number) => void
  onViewDetails: (card: ISignalProviderListCard) => void
  onDeactivate: (card: ISignalProviderListCard) => void
  onReactivate: (card: ISignalProviderListCard) => void
}

function sortableHeaderCell(
  column: SignalProviderUserSortColumn,
  label: string,
  testId: string,
  sort: SignalProviderSortState,
  onSortChange: (next: SignalProviderSortState) => void,
  truncateLabel = true,
) {
  return (
    <SortableTableHeaderLabel
      label={label}
      sortDirection={sortDirectionForColumn(sort, column)}
      testId={testId}
      truncateLabel={truncateLabel}
      onToggle={() => onSortChange(toggleSortColumn(sort, column))}
    />
  )
}

function RowActions({
  card,
  onViewDetails,
  onDeactivate,
  onReactivate,
}: {
  card: ISignalProviderListCard
  onViewDetails: (card: ISignalProviderListCard) => void
  onDeactivate: (card: ISignalProviderListCard) => void
  onReactivate: (card: ISignalProviderListCard) => void
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'flex-end' }}>
      <IconButton
        size="small"
        sx={{ p: 0 }}
        aria-label="View signal provider details"
        data-testid={`signal-provider-view-${card.platformUserId}`}
        onClick={() => onViewDetails(card)}
      >
        <EyeActionIcon />
      </IconButton>
      <IconButton
        size="small"
        sx={{ p: 0 }}
        aria-label="Signal provider actions"
        data-testid={`signal-provider-actions-${card.platformUserId}`}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <MoreVertIcon sx={{ fontSize: 20, color: '#4a5565' }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: signalProviderRowActionsMenuPaperSx,
          },
        }}
      >
        {card.accountStatus === 'active' ? (
          <MenuItem
            data-testid={`signal-provider-deactivate-menu-${card.platformUserId}`}
            sx={signalProviderDeactivateMenuItemSx}
            onClick={() => {
              setAnchorEl(null)
              onDeactivate(card)
            }}
          >
            Deactivate Account
          </MenuItem>
        ) : (
          <MenuItem
            data-testid={`signal-provider-reactivate-menu-${card.platformUserId}`}
            sx={signalProviderReactivateMenuItemSx}
            onClick={() => {
              setAnchorEl(null)
              onReactivate(card)
            }}
          >
            Reactivate Account
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}

export function SignalProviderListTable({
  rows,
  totalFiltered,
  page,
  rowsPerPage,
  sort,
  onSortChange,
  onPageChange,
  onRowsPerPageChange,
  onViewDetails,
  onDeactivate,
  onReactivate,
}: Props) {
  const totalPages = Math.max(1, Math.ceil(totalFiltered / rowsPerPage))
  const rangeStart = totalFiltered === 0 ? 0 : page * rowsPerPage + 1
  const rangeEnd = totalFiltered === 0 ? 0 : Math.min((page + 1) * rowsPerPage, totalFiltered)

  return (
    <Box data-testid="signal-provider-list-table">
      <Box sx={sensemakerTableCardSx}>
        <Table size="small" sx={sensemakerTableSx}>
          <colgroup>
            {SP_TABLE_COL_WIDTHS.map((width, index) => (
              <col key={index} style={{ width }} />
            ))}
          </colgroup>
          <TableHead
            sx={{
              bgcolor: SENSEMAKER_TABLE.headerBg,
              '& .MuiTableCell-head': {
                color: SENSEMAKER_TABLE.headerText,
                bgcolor: SENSEMAKER_TABLE.headerBg,
              },
            }}
          >
            <TableRow>
              <TableCell sx={noEllipsisHeaderCellSx}>SP ID</TableCell>
              <TableCell sx={sensemakerTableHeaderCellSx}>
                {sortableHeaderCell(
                  'firstName',
                  'First Name',
                  'signal-provider-sort-first-name',
                  sort,
                  onSortChange,
                )}
              </TableCell>
              <TableCell sx={sensemakerTableHeaderCellSx}>
                {sortableHeaderCell(
                  'lastName',
                  'Last Name',
                  'signal-provider-sort-last-name',
                  sort,
                  onSortChange,
                )}
              </TableCell>
              <TableCell sx={noEllipsisHeaderCellSx}>
                {sortableHeaderCell(
                  'state',
                  'State',
                  'signal-provider-sort-state',
                  sort,
                  onSortChange,
                  false,
                )}
              </TableCell>
              <TableCell sx={sensemakerTableHeaderCellSx}>Email</TableCell>
              <TableCell sx={sensemakerTableHeaderCellSx}>Phone</TableCell>
              <TableCell sx={noEllipsisHeaderCellSx}>
                {sortableHeaderCell(
                  'verifiedStatus',
                  'Verified Status',
                  'signal-provider-sort-verified-status',
                  sort,
                  onSortChange,
                  false,
                )}
              </TableCell>
              <TableCell sx={{ ...noEllipsisHeaderCellSx, textAlign: 'right' }}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((card) => (
              <TableRow
                key={card.platformUserId}
                data-testid={`signal-provider-row-${card.platformUserId}`}
                sx={{ '&:last-child td': { borderBottom: 0 } }}
              >
                <TableCell sx={spIdCellSx}>
                  {card.signalProviderId ?? '—'}
                </TableCell>
                <TableCell
                  sx={{
                    ...sensemakerTableBodyCellSx,
                    fontWeight: 400,
                    color: SENSEMAKER_TABLE.primaryTextColor,
                  }}
                >
                  {card.firstName}
                </TableCell>
                <TableCell
                  sx={{
                    ...sensemakerTableBodyCellSx,
                    fontWeight: 400,
                    color: SENSEMAKER_TABLE.primaryTextColor,
                  }}
                >
                  {card.lastName}
                </TableCell>
                <TableCell
                  sx={{
                    ...sensemakerTableBodyCellSx,
                    fontWeight: 400,
                    color: SENSEMAKER_TABLE.primaryTextColor,
                  }}
                >
                  {card.state ?? '—'}
                </TableCell>
                <TableCell
                  sx={{
                    ...sensemakerTableBodyCellSx,
                    fontWeight: 400,
                    color: SENSEMAKER_TABLE.secondaryTextColor,
                  }}
                >
                  {card.email}
                </TableCell>
                <TableCell
                  sx={{
                    ...sensemakerTableBodyCellSx,
                    fontWeight: 400,
                    color: SENSEMAKER_TABLE.secondaryTextColor,
                  }}
                >
                  {formatUsPhoneCell(card.phone)}
                </TableCell>
                <TableCell sx={verifiedStatusCellSx}>
                  <SignalProviderVerifiedPill status={card.verifiedStatus} />
                </TableCell>
                <TableCell sx={sensemakerTableBodyCellInteractiveSx}>
                  <RowActions
                    card={card}
                    onViewDetails={onViewDetails}
                    onDeactivate={onDeactivate}
                    onReactivate={onReactivate}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '16px',
          mt: `${SP_LIST_TABLE_PAGINATION_TOP_GAP_PX}px`,
          pr: '8px',
        }}
        data-testid="signal-provider-list-pagination"
      >
        <Typography sx={{ fontSize: '14px', lineHeight: '21px', color: '#4a5565' }}>
          Rows per page:
        </Typography>
        <Select
          value={rowsPerPage}
          onChange={(e) => {
            onRowsPerPageChange(Number(e.target.value))
            onPageChange(0)
          }}
          IconComponent={SelectDropdownIcon}
          sx={{
            minWidth: 72,
            height: 36,
            fontSize: '14px',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e1e7f3' },
          }}
        >
          {SP_LIST_ROWS_PER_PAGE_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
        <Typography sx={{ fontSize: '14px', lineHeight: '21px', color: '#4a5565' }}>
          {totalFiltered === 0 ? '0 of 0' : `${rangeStart}–${rangeEnd} of ${totalFiltered}`}
        </Typography>
        <Box sx={{ display: 'flex' }}>
          <IconButton
            size="small"
            disabled={page <= 0}
            onClick={() => onPageChange(Math.max(0, page - 1))}
            aria-label="Previous page"
          >
            <NavigateBeforeIcon sx={{ fontSize: 24, color: '#4a5565' }} />
          </IconButton>
          <IconButton
            size="small"
            disabled={page >= totalPages - 1 || totalFiltered === 0}
            onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
            aria-label="Next page"
          >
            <NavigateNextIcon sx={{ fontSize: 24, color: '#4a5565' }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  )
}

export { SP_LIST_DEFAULT_ROWS_PER_PAGE }
